import { Component, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { selectIsAuthenticated } from '@core/auth/auth.selectors';
import { selectUniversalSearch } from '@app/generated/feature-flags.selectors';
import {
  selectCurrentProject,
  selectUnderProjectRoute,
} from '@core/state/entities/project/project.selectors';
import { selectCurrentStudyId } from '@core/state/ui/stage/stage-ui.selectors';
import { SearchContext } from './search-catalog';

@Component({
  selector: 'syrf-search-trigger',
  imports: [MatButtonModule, MatIconModule],
  styles: `
    :host {
      display: inline-flex;
      color: inherit;
    }
    button {
      color: inherit;
    }
  `,
  // Angular host binding syntax is not a JavaScript identifier.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  host: { '(document:keydown)': 'shortcut($event)' },
  template: `
    @if (authenticated() && enabled()) {
      @if (compact()) {
        <button mat-icon-button aria-label="Search SyRF" (click)="open()">
          <mat-icon>search</mat-icon>
        </button>
      } @else {
        <button mat-button aria-label="Search SyRF" (click)="open()">
          <mat-icon>search</mat-icon>Search SyRF
        </button>
      }
    }
  `,
})
export class SearchTriggerComponent {
  readonly compact = input(false);
  private readonly _store = inject(Store);
  private readonly _dialog = inject(MatDialog);
  private readonly _project = this._store.selectSignal(selectCurrentProject);
  private readonly _underProject = this._store.selectSignal(
    selectUnderProjectRoute
  );
  private readonly _studyId = this._store.selectSignal(selectCurrentStudyId);
  private _opening = false;
  protected readonly authenticated = this._store.selectSignal(
    selectIsAuthenticated
  );
  protected readonly enabled = this._store.selectSignal(selectUniversalSearch);

  protected shortcut(event: KeyboardEvent): void {
    if (
      !event.repeat &&
      (event.ctrlKey || event.metaKey) &&
      !event.altKey &&
      event.key.toLowerCase() === 'k' &&
      this.authenticated() &&
      this.enabled() &&
      !this._dialog.openDialogs.length
    ) {
      event.preventDefault();
      void this.open();
    }
  }
  protected async open(): Promise<void> {
    if (
      this._opening ||
      !this.authenticated() ||
      !this.enabled() ||
      this._dialog.openDialogs.length
    )
      return;
    this._opening = true;
    try {
      const searchModule = await import('./universal-search-dialog.component');
      if (
        !this.authenticated() ||
        !this.enabled() ||
        this._dialog.openDialogs.length
      )
        return;
      const project = this._underProject() ? this._project() : null;
      const data: SearchContext = {
        projectId: project?.id ?? null,
        projectName: project?.name ?? null,
        studyId: project ? this._studyId() : null,
      };
      this._dialog.open(searchModule.UniversalSearchDialogComponent, {
        data,
        width: '880px',
        maxWidth: '100vw',
        maxHeight: '100dvh',
        autoFocus: 'input',
        restoreFocus: true,
        ariaLabel: 'Search SyRF',
        closeOnNavigation: true,
      });
    } finally {
      this._opening = false;
    }
  }
}
